import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import geoFenceService from '../../../services/geoFenceService';
import clientSiteService from '../../../services/clientSiteService';
import NotificationToast from '../../../components/ui/NotificationToast';

const GeoFenceManagement = ({ clientId }) => {
  const [geoFences, setGeoFences] = useState([]);
  const [clientSites, setClientSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    latitude: '',
    longitude: '',
    radius: '100',
    address: '',
    status: 'active',
    client_site_id: ''
  });
  const [currentLocation, setCurrentLocation] = useState(null);

  useEffect(() => {
    loadGeoFences();
    loadClientSites();
  }, [clientId]);

  const loadClientSites = async () => {
    try {
      const filters = { is_active: 'true' };
      if (clientId) {
        filters.client_id = clientId;
      }
      const result = await clientSiteService.getClientSites(filters);
      if (result?.success) {
        setClientSites(result.data || []);
      }
    } catch (error) {
      console.log('Error loading client sites:', error);
    }
  };

  const loadGeoFences = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (clientId) {
        filters.client_id = clientId;
      }
      const result = await geoFenceService.getGeoFences(filters);
      if (result?.success) {
        setGeoFences(result.data || []);
      }
    } catch (error) {
      console.log('Error loading geo-fences:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setNotification({ message: 'Geolocation is not supported by your browser', type: 'error' });
      return;
    }

    setNotification({ message: 'Getting your location...', type: 'info' });
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setFormData(prev => ({
          ...prev,
          latitude: position.coords.latitude.toString(),
          longitude: position.coords.longitude.toString()
        }));
        setNotification({ message: 'Location captured successfully', type: 'success' });
      },
      (error) => {
        setNotification({ message: 'Failed to get location: ' + error.message, type: 'error' });
      }
    );
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEdit = (fence) => {
    console.log('Editing geo-fence:', fence);
    setEditingId(fence.id);
    const formDataToSet = {
      name: fence.name || '',
      description: fence.description || '',
      latitude: fence.latitude?.toString() || '',
      longitude: fence.longitude?.toString() || '',
      radius: fence.radius?.toString() || '100',
      address: fence.address || '',
      status: fence.status || 'active',
      client_site_id: fence.client_site_id || ''
    };
    console.log('Setting form data:', formDataToSet);
    setFormData(formDataToSet);
    setShowForm(true);
    // Scroll to form after a brief delay to ensure it's rendered
    setTimeout(() => {
      const formElement = document.querySelector('[data-geo-fence-form]');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      name: '',
      description: '',
      latitude: '',
      longitude: '',
      radius: '100',
      address: '',
      status: 'active',
      client_site_id: ''
    });
    setCurrentLocation(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.latitude || !formData.longitude || !formData.radius) {
      setNotification({ message: 'Please fill all required fields', type: 'error' });
      return;
    }

    try {
      const data = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        radius: parseFloat(formData.radius)
      };

      console.log('Submitting geo-fence:', { editingId, data });
      const result = editingId
        ? await geoFenceService.updateGeoFence(editingId, data)
        : await geoFenceService.createGeoFence(data);
      
      console.log('Submit result:', result);

      if (result?.success) {
        setNotification({ 
          message: `Geo-fence ${editingId ? 'updated' : 'created'} successfully`, 
          type: 'success' 
        });
        handleCancel();
        loadGeoFences();
      } else {
        const errorMessage = result?.error || result?.message || `Failed to ${editingId ? 'update' : 'create'} geo-fence`;
        setNotification({ 
          message: errorMessage, 
          type: 'error' 
        });
        console.error('Geo-fence save error:', result);
      }
    } catch (error) {
      const errorMessage = error.message || 'An error occurred while saving the geo-fence';
      setNotification({ message: errorMessage, type: 'error' });
      console.error('Error saving geo-fence:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this geo-fence?')) {
      return;
    }

    try {
      const result = await geoFenceService.deleteGeoFence(id);
      if (result?.success) {
        setNotification({ message: 'Geo-fence deleted successfully', type: 'success' });
        loadGeoFences();
      } else {
        setNotification({ message: result?.error || 'Failed to delete geo-fence', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
      console.log('Error deleting geo-fence:', error);
    }
  };

  return (
    <div className="p-6">
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        onDismiss={() => setNotification(null)}
      />

      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="text-lg font-semibold text-foreground">Geo-fence Locations</h4>
          <p className="text-sm text-muted-foreground">Manage attendance location boundaries</p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
            <Icon name="Plus" size={16} />
            Add Geo-fence
          </Button>
        )}
      </div>

      {showForm && (
        <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border" data-geo-fence-form>
          <h5 className="font-semibold text-foreground mb-4">
            {editingId ? 'Edit Geo-fence' : 'Add New Geo-fence'}
          </h5>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Main Office, Branch Office"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description
                </label>
                <Input
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Optional description"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Address
                </label>
                <Input
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Full address of the location"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Latitude *
                </label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => handleInputChange('latitude', e.target.value)}
                    placeholder="28.6139"
                    required
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={getCurrentLocation}
                    className="flex items-center gap-2"
                    title="Use current location"
                  >
                    <Icon name="MapPin" size={16} />
                    Current
                  </Button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Longitude *
                </label>
                <Input
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => handleInputChange('longitude', e.target.value)}
                  placeholder="77.2090"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Radius (meters) *
                </label>
                <Input
                  type="number"
                  value={formData.radius}
                  onChange={(e) => handleInputChange('radius', e.target.value)}
                  placeholder="100"
                  min="10"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Allowed distance from center point (in meters)
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Link to Client Site (Optional)
                </label>
                <select
                  value={formData.client_site_id}
                  onChange={(e) => handleInputChange('client_site_id', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="">No client site (standalone geo-fence)</option>
                  {clientSites.map(site => (
                    <option key={site.id} value={site.id}>
                      {site.site_name} - {site.company_name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Link this geo-fence to a client site for business-aligned attendance validation
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit" className="flex items-center gap-2">
                <Icon name="Save" size={16} />
                {editingId ? 'Update' : 'Create'}
              </Button>
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading geo-fences...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {geoFences.length === 0 ? (
            <div className="col-span-full text-center py-8 text-muted-foreground">
              No geo-fences found. Click "Add Geo-fence" to create one.
            </div>
          ) : (
            geoFences.map(fence => (
              <div key={fence.id} className="p-4 border border-border rounded-lg hover:bg-muted/50">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h5 className="font-semibold text-foreground flex items-center gap-2">
                      {fence.name}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        fence.status === 'active' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                      }`}>
                        {fence.status}
                      </span>
                    </h5>
                    {fence.description && (
                      <p className="text-sm text-muted-foreground mt-1">{fence.description}</p>
                    )}
                    {fence.address && (
                      <p className="text-xs text-muted-foreground mt-1">{fence.address}</p>
                    )}
                    <div className="mt-2 text-xs text-muted-foreground">
                      <p>📍 {fence.latitude}, {fence.longitude}</p>
                      <p>📏 Radius: {fence.radius}m</p>
                      {fence.client_site_name && (
                        <p className="mt-1 text-blue-600 dark:text-blue-400">
                          🔗 Linked to: {fence.client_site_name}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(fence)}
                      className="h-8 w-8 p-0"
                      title="Edit"
                    >
                      <Icon name="Edit" size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(fence.id)}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      title="Delete"
                    >
                      <Icon name="Trash2" size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default GeoFenceManagement;

